import { markRaw } from 'vue';
import type { PerImageCocoDoc, CocoAnnotation } from '@/models/RobVision';
import { rasterizePolygon } from '@/helpers/MaskAnalysis';
import {
  maskToCocoPolygons,
  maskBBox,
  maskArea,
  maskCentroid,
  maskHasPixels,
} from '@/helpers/MaskToPolygon';

/** One object instance being edited: mask raster in image resolution plus
 * the per-instance attributes of the RobLabel annotation model. */
export interface EditorInstance {
  id: number;
  categoryName: string;
  mask: Uint8Array;
  // Reactive mirror of "the mask has foreground pixels": the mask bytes are
  // markRaw'd for edit performance, so computed props (e.g. the ready-mark
  // guard) cannot observe them directly. Maintained at every edit site.
  hasMask: boolean;
  keypoint: [number, number] | null; // pixel coordinates
  angleDeg: number | null;
  source: 'prelabel' | 'human';
  // A human edit clears the AI badge and marks the instance reviewed.
  touched: boolean;
}

/**
 * Set the keypoint to the mask centroid when the mask first appears — a
 * good first guess that prevents half-labeled instances. An existing
 * keypoint is never moved, however the mask changes.
 */
export function ensureKeypoint(
  instance: EditorInstance,
  width: number,
  height: number,
): void {
  if (instance.keypoint) {
    return;
  }
  instance.keypoint = maskCentroid(instance.mask, width, height);
}

// One distinct color per object number, looping.
export const INSTANCE_COLORS = [
  '#2563EB',
  '#DB746E',
  '#1B7A5C',
  '#DF4C85',
  '#B26500',
  '#7199F4',
  '#0B6B66',
  '#A14D2E',
];

export function instanceColor(id: number): string {
  return INSTANCE_COLORS[id % INSTANCE_COLORS.length];
}

/** Serialize the session into the per-image COCO doc the dataset build
 * consumes. Instances without mask pixels are skipped. The keypoint defaults
 * to the bbox center (mirrors the current pipeline behavior). */
export function instancesToCocoDoc(
  instances: EditorInstance[],
  fileName: string,
  width: number,
  height: number,
  rotationEnabled: boolean,
): PerImageCocoDoc {
  const categories: string[] = [];
  const annotations: CocoAnnotation[] = [];
  for (const instance of instances) {
    const bbox = maskBBox(instance.mask, width, height);
    if (!bbox || !instance.categoryName) {
      continue;
    }
    const segmentation = maskToCocoPolygons(instance.mask, width, height);
    if (segmentation.length === 0) {
      // A few stray pixels below the polygonizer's noise threshold — the
      // backend rejects empty segmentations (400), so skip like no-mask.
      continue;
    }
    let categoryId = categories.indexOf(instance.categoryName) + 1;
    if (categoryId === 0) {
      categories.push(instance.categoryName);
      categoryId = categories.length;
    }
    const keypoint = instance.keypoint ?? [
      bbox[0] + bbox[2] / 2,
      bbox[1] + bbox[3] / 2,
    ];
    const annotation: CocoAnnotation = {
      id: annotations.length + 1,
      image_id: 1,
      category_id: categoryId,
      segmentation,
      bbox,
      area: maskArea(instance.mask),
      keypoints: [keypoint[0], keypoint[1], 2],
    };
    if (rotationEnabled && instance.angleDeg !== null) {
      annotation.rotation = instance.angleDeg;
    }
    annotations.push(annotation);
  }
  return {
    images: [{ id: 1, file_name: fileName, width, height }],
    annotations,
    categories: categories.map((name, index) => ({ id: index + 1, name })),
  };
}

function rasterizeSegmentation(
  segmentation: number[][],
  width: number,
  height: number,
): Uint8Array {
  const mask = new Uint8Array(width * height);
  for (const flat of segmentation) {
    const vertices: Array<[number, number]> = [];
    for (let i = 0; i + 1 < flat.length; i += 2) {
      vertices.push([flat[i], flat[i + 1]]);
    }
    rasterizePolygon(vertices, mask, width, height);
  }
  // Masks stay out of Vue's reactivity: per-pixel access through a reactive
  // proxy is 10-50x slower, which makes brushing lag.
  return markRaw(mask);
}

/** Rebuild editor instances from a saved per-image COCO doc. */
export function cocoDocToInstances(
  doc: PerImageCocoDoc,
  width: number,
  height: number,
): EditorInstance[] {
  const categoryNames = new Map<number, string>();
  for (const category of doc.categories ?? []) {
    categoryNames.set(category.id, category.name);
  }
  const instances: EditorInstance[] = [];
  for (const annotation of doc.annotations ?? []) {
    if (!Array.isArray(annotation.segmentation)) {
      continue;
    }
    const { keypoints } = annotation;
    instances.push({
      id: instances.length,
      categoryName: categoryNames.get(annotation.category_id) ?? '',
      mask: rasterizeSegmentation(annotation.segmentation, width, height),
      keypoint:
        Array.isArray(keypoints) && keypoints.length >= 2
          ? [keypoints[0], keypoints[1]]
          : null,
      angleDeg:
        typeof annotation.rotation === 'number' ? annotation.rotation : null,
      source: 'human',
      touched: true,
      // Saved annotations always carry a non-empty segmentation.
      hasMask: true,
    });
  }
  return instances;
}

/**
 * Decode an edge-detection mask into a full-image raster. The mask is a
 * COCO-style uncompressed RLE (column-major counts, starting with a
 * background run) covering only the detection's bounding-box region —
 * mask.width/height equal the bbox's, and bbox.x/y place it in the image.
 */
function rleMaskToRaster(
  counts: number[],
  maskWidth: number,
  maskHeight: number,
  offsetX: number,
  offsetY: number,
  imageWidth: number,
  imageHeight: number,
): Uint8Array {
  const raster = new Uint8Array(imageWidth * imageHeight);
  const maxIndex = maskWidth * maskHeight;
  let index = 0;
  let foreground = false;
  for (const run of counts) {
    if (index >= maxIndex || run > maxIndex) {
      // Corrupt/hostile RLE from info.json: one absurd count would spin the
      // inner loop for minutes — stop decoding, keep what parsed so far.
      break;
    }
    if (foreground) {
      for (let i = 0; i < run; i++) {
        const j = index + i;
        const col = Math.floor(j / maskHeight);
        if (col >= maskWidth) {
          // Corrupt RLE: the counts overrun the detection's bbox region —
          // discard instead of writing into columns right of it.
          break;
        }
        const x = offsetX + col;
        const y = offsetY + (j % maskHeight);
        if (x >= 0 && x < imageWidth && y >= 0 && y < imageHeight) {
          raster[y * imageWidth + x] = 255;
        }
      }
    }
    index += run;
    foreground = !foreground;
  }
  return markRaw(raster);
}

/**
 * Build instances from the edge prelabels in info.json (`detections`).
 * Edge schema (data-logging spec): `className`, `bbox: {x, y, width,
 * height}`, `mask: {counts, width, height}` (bbox-local RLE),
 * `rotationDegrees`. Entries without a decodable mask are skipped.
 */
export function prelabelsToInstances(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  info: any,
  projectClasses: string[],
  width: number,
  height: number,
): EditorInstance[] {
  const detections = Array.isArray(info?.detections) ? info.detections : [];
  const instances: EditorInstance[] = [];
  for (const detection of detections) {
    const bbox = detection?.bbox;
    const rle = detection?.mask;
    // The edge serializes RLE counts either as a number array or as one
    // space-separated string, depending on the export version.
    const counts: number[] | null = Array.isArray(rle?.counts)
      ? rle.counts
      : typeof rle?.counts === 'string'
        ? rle.counts.split(/\s+/).filter(Boolean).map(Number)
        : null;
    if (
      !counts ||
      counts.some(Number.isNaN) ||
      typeof bbox?.x !== 'number' ||
      typeof bbox?.y !== 'number'
    ) {
      continue;
    }
    const rawName = detection.className;
    // The keypoint IS the pick point. Edge output carries no pixel-space
    // keypoint field (only a 3D pose), so it defaults to the bbox center —
    // mirroring the pipeline's auto-generated keypoint — and stays visible
    // and adjustable. An explicit `keypoints` field wins when present.
    const explicitKeypoint = detection.keypoints ?? detection.keypoint;
    const keypoint: [number, number] =
      Array.isArray(explicitKeypoint) && explicitKeypoint.length >= 2
        ? [explicitKeypoint[0], explicitKeypoint[1]]
        : [bbox.x + (bbox.width ?? 0) / 2, bbox.y + (bbox.height ?? 0) / 2];
    instances.push({
      id: instances.length,
      categoryName:
        typeof rawName === 'string' && projectClasses.includes(rawName)
          ? rawName
          : (projectClasses[0] ?? ''),
      mask: rleMaskToRaster(
        counts,
        rle.width ?? bbox.width,
        rle.height ?? bbox.height,
        bbox.x,
        bbox.y,
        width,
        height,
      ),
      keypoint,
      angleDeg:
        typeof detection.rotationDegrees === 'number'
          ? ((detection.rotationDegrees + 540) % 360) - 180
          : null,
      source: 'prelabel',
      touched: false,
      // Corrupt RLE can decode to nothing — the flag must reflect the raster.
      hasMask: false,
    });
    const added = instances[instances.length - 1];
    added.hasMask = maskHasPixels(added.mask);
  }
  return instances;
}
